<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import GlobalLoading from "~/components/Shared/GlobalLoading.vue";
import DebugHeadersPanel from "~/components/Debug/HeadersPanel.vue";
import DirectDebugOverlay from "~/components/Debug/DirectDebugOverlay.vue";
import CacheDebug from "~/components/Debug/CacheDebug.vue";
import { useAuth } from "~/composables/useAuth";
import type { ExtraPackType, ExtraPackMoreType } from "@/types";

// Add globalLogout to the Window interface
declare global {
  interface Window {
    globalLogout: () => Promise<void>;
  }
}

interface ReactNativeContext {
  isApp: boolean;
  platform: string | null;
  appVersion: string | null;
  deviceId: string | null;
  authToken: string | null;
  capabilities: string[];
  theme: string;
  debugMode: boolean;
  allHeaders: Record<string, string>;
  hasCapability: (capability: string) => boolean;
  isVersionAtLeast: (version: string) => boolean;
}

interface WebViewContext {
  isWebView: boolean;
  userAgent: string | null;
  detectionMethod: string | null;
  isPlatformWebView: (platform: string) => boolean;
}

// Get Nuxt app instance
const nuxtApp = useNuxtApp();
// Access mobile state directly from payload without using definePageMeta
const isMobile = computed(() => nuxtApp.payload.isMobile);
const { data: modalState } = useModalState();
const mainStore = useMainStore();
const commerceStore = useCommerceStore();

// Create a properly typed fallback object
const fallbackReactNative: ReactNativeContext = {
  isApp: false,
  platform: null,
  appVersion: null,
  deviceId: null,
  authToken: null,
  capabilities: [],
  theme: "light",
  debugMode: false,
  allHeaders: {},
  hasCapability: () => false,
  isVersionAtLeast: () => false,
};

// Create fallback for WebView
const fallbackWebView: WebViewContext = {
  isWebView: false,
  userAgent: null,
  detectionMethod: null,
  isPlatformWebView: () => false,
};

// Safely cast to ReactNativeContext with proper type checking
const reactNative =
  (nuxtApp.$reactNative as unknown as ReactNativeContext) ||
  fallbackReactNative;

// Get WebView context
const webView =
  (nuxtApp.$webView as unknown as WebViewContext) || fallbackWebView;

// Use WebView composable for comprehensive handling
const { initializeWebView } = useWebView();

// Combined check for hiding navigation elements
const shouldHideNavigation = computed(() => {
  return reactNative.isApp || webView.isWebView;
});

const isDebugMode = computed(() => {
  if (typeof window !== "undefined") {
    const forceDebug = localStorage.getItem("force-debug-mode") === "true";
    if (forceDebug) return true;
    const headerDebug = localStorage.getItem("x-go-debugmode") === "true";
    if (headerDebug) return true;
  }
  return reactNative.debugMode === true;
});

const globalLoading = ref(true);
const clientReady = ref(false);
const config = useRuntimeConfig();

onMounted(() => {
  clientReady.value = true;
  globalLoading.value = true;

  if (window.flutter_inappwebview != null) {
    // Define a global logout function
    window.globalLogout = async () => {
      try {
        const { signOut } = useAuth();
        await signOut();
        console.log("User successfully logged out.");
        window.location.href = "/account"; // App will detect this and handle logout
      } catch (error) {
        console.error("Error during logout:", error);
      }
    };
  }

  // Initialize WebView handling
  initializeWebView();

  // Add webview class to body if in WebView
  if (webView.isWebView) {
    document.body.classList.add("webview");
    console.log("WebView detected - added webview class to body");

    // Load WebView-specific CSS
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "/css/webview-overrides.css";
    document.head.appendChild(link);
  }

  // Enhanced scroll to top - handled by scroll-to-top.client.ts plugin
  // Force immediate scroll and trigger global scroll-to-top function
  if (typeof window !== "undefined" && (window as any).forceScrollToTop) {
    (window as any).forceScrollToTop();
  }
});

onMounted(() => {
  // Check if we're in a React Native environment
  const { isReactNativeWebView } = useClient();

  if (isReactNativeWebView.value) {
    console.log("Genesys disabled: React Native environment detected");
    return; // Exit early - do not load Genesys for React Native clients
  }

  console.log("Initializing Genesys widget with WebView compatibility support");

  // Use WebView-compatible initialization for desktop and mobile web browsers only
  import("~/utils/genesysWebViewProxy")
    .then(({ initializeGenesysWithWebViewSupport }) => {
      initializeGenesysWithWebViewSupport();
    })
    .catch((error) => {
      console.error("Failed to load Genesys WebView proxy:", error);

      // Fallback initialization if WebView support fails
      const genesysScript = document.createElement("script");
      genesysScript.type = "text/javascript";
      genesysScript.charset = "utf-8";

      const config = useRuntimeConfig();

      genesysScript.innerHTML = `
      (function (g, e, n, es, ys) {
        g['_genesysJs'] = e;
        g[e] = g[e] || function () {
          (g[e].q = g[e].q || []).push(arguments)
        };
        g[e].t = 1 * new Date();
        g[e].c = es;
        ys = document.createElement('script'); ys.async = 1; ys.src = n; ys.charset = 'utf-8'; document.head.appendChild(ys);
      })(window, 'Genesys', 'https://apps.mypurecloud.com.au/genesys-bootstrap/genesys.min.js', {
        environment: 'prod-apse2',
        deploymentId: '${config.public.GENESYS_DEPLOYMENT_ID}'
      });
    `;

      document.head.appendChild(genesysScript);
    });
});

const { fetchSettings } = useGlobalSettings();
const { fetchFaqs } = useFaqs();
const { fetchNotifications } = useNotifications();
const { fetchStrapiProducts } = useStrapiProducts();
const { fetchProducts } = useProducts();
const { fetchExtraPacks } = useExtraPacks();
const { fetchExtraPacksMore } = useExtraPacksMore();

globalLoading.value = false;

const loadCriticalData = async () => {
  const route = useRoute();

  // Global Settings - always needed across all routes
  const existingSettings = mainStore.getGlobalSettings;
  if (!existingSettings) {
    const resultSettings = await fetchSettings();
    if (resultSettings) {
      mainStore.updateGlobalSettings(resultSettings);
    }
  }

  // Fetch data based on route

  // FAQ data - only load on FAQ pages unless already in store
  const existingFaqs = mainStore.getFaqs;
  if (!existingFaqs || existingFaqs.length === 0) {
    const resultFaqs = await fetchFaqs();
    if (resultFaqs) {
      mainStore.setFaqs(resultFaqs);
    }
  }

  // Notifications data - only load on homepage
  if (route.fullPath === "/") {
    const resultNotifications = await fetchNotifications();
    if (resultNotifications) mainStore.setNotifications(resultNotifications);
  }

  // Products data - only load on homepage or plans page
  if (route.fullPath === "/" || route.fullPath === "/mobile-plans") {
    // Product data uses commerce store for caching
    const resultStrapiProducts = await fetchStrapiProducts();
    if (resultStrapiProducts)
      commerceStore.updateProductsWithStrapi(resultStrapiProducts);

    const resultProducts = await fetchProducts();
    if (resultProducts) commerceStore.updateProducts(resultProducts);
  }

  // Extrapacks data - only load on extrapacks page
  if (route.fullPath === "/extra-packs") {
    const resultExtraPacks = await fetchExtraPacks();
    // Ensure we have an array before setting in the store
    if (resultExtraPacks && Array.isArray(resultExtraPacks)) {
      // Explicit type assertion to help TypeScript understand this is the correct type
      mainStore.setExtraPacks(resultExtraPacks as ExtraPackType[]);
    }

    const resultExtraPacksMore = await fetchExtraPacksMore();
    // Ensure we have an array before setting in the store
    if (resultExtraPacksMore && Array.isArray(resultExtraPacksMore)) {
      // Explicit type assertion to help TypeScript understand this is the correct type
      mainStore.setExtraPacksMore(resultExtraPacksMore as ExtraPackMoreType[]);
    }
  }
};

loadCriticalData();
</script>

<template>
  <div>
    <div class="page-container min-h-screen bg-background overflow-auto">
      <NuxtLoadingIndicator color="#e01a22" />
      <Transition name="fade">
        <SharedModal v-if="modalState" />
      </Transition>
      <!-- Only show header/footer when not in React Native app or WebView -->
      <template v-if="!shouldHideNavigation">
        <SharedHeader />
        <SharedTopNotification />
      </template>

      <!-- Page content always shows -->
      <NuxtPage />

      <!-- Only show footer/navigation when not in React Native app or WebView -->
      <template v-if="!shouldHideNavigation">
        <SharedFooter v-if="!isMobile" />
        <NavigationMobileBottom class="lg:hidden" />
      </template>

      <client-only>
        <DirectDebugOverlay
          :headers="reactNative.allHeaders || {}"
          :is-debug-mode="isDebugMode"
          :is-react-native="reactNative.isApp"
          :platform="reactNative.platform"
          :app-version="reactNative.appVersion"
          :device-id="reactNative.deviceId"
        />
        <CacheDebug />
      </client-only>
    </div>
  </div>
</template>

<style>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap");

:root {
  font-family:
    "Montserrat",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    "Helvetica Neue",
    Arial,
    sans-serif !important;
}

body {
  font-family:
    "Montserrat",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    "Helvetica Neue",
    Arial,
    sans-serif !important;
}

html {
  font-size: 14px;
  height: 100%;
  overflow-x: hidden;
  scroll-behavior: auto;
}

body {
  height: 100%;
  overflow-x: hidden;
  position: relative;
  padding-bottom: 60px;
  margin: 0;
  scroll-behavior: auto;
}

.page-container {
  min-height: 100vh;
  padding-bottom: 60px;
  position: relative;
  overflow-x: hidden;
  overflow-anchor: none;
}

* {
  overflow-anchor: none;
}

.mobile-footer,
.mobile-nav-fixed {
  position: fixed !important;
  bottom: 0 !important;
  left: 0 !important;
  right: 0 !important;
  width: 100% !important;
  height: 60px !important;
  z-index: 9999 !important;
  background-color: #9900f1 !important;
  box-shadow: 0 -1px 4px rgba(0, 0, 0, 0.1) !important;
}

@media (min-width: 768px) {
  .mobile-footer,
  .mobile-nav-fixed {
    display: none !important;
  }
}
</style>
